import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { ukDate } from "@/lib/format";
import { toast } from "sonner";
import { Star, Trash2, Eye, EyeOff } from "lucide-react";

function Stars({ n }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const load = useCallback(async () => { const { data } = await api.get("/reviews"); setReviews(data); }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => { await api.patch(`/reviews/${id}/publish`); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this review?")) return; await api.delete(`/reviews/${id}`); toast.success("Deleted"); load(); };

  const published = reviews.filter((r) => r.is_published);
  const avg = published.length ? (published.reduce((a, r) => a + r.rating, 0) / published.length).toFixed(1) : "—";

  return (
    <div className="max-w-4xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><h1 className="font-extrabold text-3xl">Reviews</h1>
          <p className="text-stone-600 text-sm mt-1">{published.length} published · average {avg} ★ · new client reviews start hidden — publish the ones you want on your booking page</p></div>
      </div>

      <div className="mt-5 space-y-3">
        {reviews.length === 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center text-stone-500">
            No reviews yet. Clients can leave a review after booking on your public page.
          </div>
        )}
        {reviews.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className={`bg-white rounded-2xl border border-stone-200 p-5 shadow-sm ${r.is_published ? "" : "opacity-60"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><Stars n={r.rating} /><span className="font-semibold">{r.client_name}</span></div>
                {r.comment && <p className="text-sm text-stone-600 mt-1.5">{r.comment}</p>}
                <div className="text-xs text-stone-400 mt-1.5">{r.staff_name ? `${r.staff_name} · ` : ""}{ukDate(r.created_at)}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`review-publish-${r.id}`} onClick={() => toggle(r.id)} title={r.is_published ? "Hide" : "Publish"}
                  className="h-8 w-8 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50">
                  {r.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-stone-400" />}
                </button>
                <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)}
                  className="h-8 w-8 rounded-full border border-stone-200 flex items-center justify-center text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
